/* @jsx h */
import { defineComponent, h, ref } from "vue";
import { useLocalization } from "@lemmonhq/uikit";
import type { EmailBlockType, EmailTheme } from "../types";
import { Field, Segmented } from "../shared";
import { BLOCK_PALETTE, DRAG_NEW, THEME_PRESETS } from "./blocks";

type EmailPaletteProps = {
  theme: EmailTheme;
  onAdd: (type: EmailBlockType) => void;
  onThemeChange: (theme: EmailTheme) => void;
};

export const EmailPalette = defineComponent<EmailPaletteProps>({
  name: "EmailPalette",
  props: ["theme", "onAdd", "onThemeChange"],
  setup(props) {
    const { t } = useLocalization();
    const tab = ref<"blocks" | "style">("blocks");

    const patch = (p: Partial<EmailTheme>) => props.onThemeChange({ ...props.theme, ...p });

    const ColorRow = (label: string, key: keyof EmailTheme) => (
      <label class="flex items-center justify-between gap-2 text-sm">
        <span>{label}</span>
        <span class="flex items-center gap-2">
          <code class="text-xs text-base-content/50">{String(props.theme[key])}</code>
          <input
            type="color"
            class="h-7 w-9 cursor-pointer rounded border border-base-300 bg-transparent"
            value={String(props.theme[key])}
            onInput={(e) => patch({ [key]: (e.target as HTMLInputElement).value } as Partial<EmailTheme>)}
          />
        </span>
      </label>
    );

    const Blocks = () => (
      <div class="space-y-3">
        <p class="text-xs text-base-content/50">{t("campaigns.studio_email.palette_hint")}</p>
        <div class="grid grid-cols-2 gap-2">
          {BLOCK_PALETTE.map((b) => (
            <button
              key={b.type}
              type="button"
              draggable={true}
              onDragstart={(e) => {
                e.dataTransfer?.setData(DRAG_NEW, b.type);
                if (e.dataTransfer) e.dataTransfer.effectAllowed = "copy";
              }}
              onClick={() => props.onAdd(b.type)}
              class="flex cursor-grab flex-col items-center gap-1 rounded-lg border border-base-300 bg-base-100 p-3 text-xs font-medium transition hover:border-primary hover:text-primary active:cursor-grabbing"
            >
              <span class="material-symbols-outlined text-2xl">{b.icon}</span>
              {t(b.label)}
            </button>
          ))}
        </div>
      </div>
    );

    const Style = () => (
      <div class="space-y-4">
        <div>
          <div class="mb-2 text-xs font-semibold uppercase tracking-wide text-base-content/50">
            {t("campaigns.studio_email.presets")}
          </div>
          <div class="grid grid-cols-2 gap-2">
            {THEME_PRESETS.map((p) => (
              <button
                key={p.name}
                type="button"
                class="flex items-center gap-2 rounded-lg border border-base-300 p-2 text-xs hover:border-primary"
                onClick={() => patch(p.theme)}
              >
                <span class="flex">
                  <span class="h-4 w-4 rounded-full" style={{ background: p.theme.brand_color }} />
                  <span class="-ml-1 h-4 w-4 rounded-full border border-base-300" style={{ background: p.theme.background_color }} />
                </span>
                {p.name}
              </button>
            ))}
          </div>
        </div>
        <div class="space-y-2">
          {ColorRow(t("campaigns.studio_email.color_brand"), "brand_color")}
          {ColorRow(t("campaigns.studio_email.color_background"), "background_color")}
          {ColorRow(t("campaigns.studio_email.color_content"), "content_background")}
          {ColorRow(t("campaigns.studio_email.color_text"), "text_color")}
        </div>
        <Field label={t("campaigns.studio_email.font")}>
          <Segmented
            size="xs"
            value={props.theme.font}
            options={[
              { value: "sans", label: "Sans" },
              { value: "serif", label: "Serif" },
              { value: "rounded", label: "Rounded" },
            ]}
            onChange={(v) => patch({ font: v as EmailTheme["font"] })}
          />
        </Field>
        <Field label={t("campaigns.studio_email.corner_radius")} counter={`${props.theme.radius}px`}>
          <input
            type="range"
            min={0}
            max={24}
            class="range range-primary range-xs"
            value={props.theme.radius}
            onInput={(e) => patch({ radius: Number((e.target as HTMLInputElement).value) })}
          />
        </Field>
      </div>
    );

    return () => (
      <div class="flex h-full flex-col">
        <div role="tablist" class="tabs tabs-bordered shrink-0 px-2">
          <button role="tab" class={["tab", tab.value === "blocks" && "tab-active"]} onClick={() => (tab.value = "blocks")}>
            {t("campaigns.studio_email.tab_blocks")}
          </button>
          <button role="tab" class={["tab", tab.value === "style" && "tab-active"]} onClick={() => (tab.value = "style")}>
            {t("campaigns.studio_email.tab_style")}
          </button>
        </div>
        <div class="min-h-0 flex-1 overflow-y-auto p-3">{tab.value === "blocks" ? Blocks() : Style()}</div>
      </div>
    );
  },
});
